import React, { useEffect, useRef, useState } from 'react'
import { portal } from '../portalApi.js'
import { fmtDateTime } from '../api.js'

// 서랍(메모) — 화면 어디든 붙일 수 있는 부품 하나.
// target: 이 서랍이 붙는 대상의 고유 키(예: 'home'). screenKey: 그 화면의 권한 키(없으면 로그인만 필요).
// context/contextLabel: 지금 화면이 보고 있는 기간(호스트 화면이 넘겨줌) — 메모 쓸 때 자동 저장됨.
// onNavigate(context): 메모를 눌렀을 때 호스트 화면을 그 기간으로 되돌리는 콜백.
// autoOpenNoteId/onAutoOpened: 알림 클릭으로 들어왔을 때 서랍을 펼친 채로 특정 메모를 바로 보여주기 위함.

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result)
    r.onerror = reject
    r.readAsDataURL(file)
  })
}

function MentionTextarea({ value, onChange, mentionables, placeholder, onPasteImage, autoFocus }) {
  const ref = useRef(null)
  const [menu, setMenu] = useState(null) // { query, start }

  const handleChange = (e) => {
    const v = e.target.value
    onChange(v)
    const pos = e.target.selectionStart
    const before = v.slice(0, pos)
    const m = before.match(/@([^\s@]*)$/)
    if (m) setMenu({ query: m[1], start: pos - m[1].length - 1 })
    else setMenu(null)
  }

  const pick = (u) => {
    const start = menu.start
    const before = value.slice(0, start)
    const after = value.slice(ref.current.selectionStart)
    const next = `${before}@${u.shortName} ${after}`
    onChange(next)
    setMenu(null)
    setTimeout(() => ref.current?.focus(), 0)
  }

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items || []
    for (const item of items) {
      if (item.type && item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) {
          const dataUrl = await fileToDataUrl(file)
          onPasteImage(dataUrl)
        }
      }
    }
  }

  const filtered = menu
    ? mentionables.filter((u) => u.shortName.includes(menu.query) || u.name.includes(menu.query)).slice(0, 8)
    : []

  return (
    <div style={{ position: 'relative' }}>
      <textarea ref={ref} className="note-textarea" placeholder={placeholder} value={value}
        autoFocus={autoFocus} onChange={handleChange} onPaste={handlePaste} rows={3} />
      {menu && filtered.length > 0 && (
        <div className="vendor-sugs" style={{ top: '100%', left: 0 }}>
          {filtered.map((u) => (
            <button key={u.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(u)}>
              {u.shortName} <i>{u.name}</i>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function ImageStrip({ images, onRemove, onEnlarge }) {
  if (!images.length) return null
  return (
    <div className="note-image-strip">
      {images.map((src, i) => (
        <div key={i} className="note-image-thumb">
          <img src={src} onClick={() => onEnlarge(src)} alt="" />
          {onRemove && <button type="button" onClick={() => onRemove(i)}>×</button>}
        </div>
      ))}
    </div>
  )
}

function NoteCard({ note, mentionables, onReply, onDelete, currentUserId, onEnlarge }) {
  const [replyText, setReplyText] = useState('')
  const [replyImages, setReplyImages] = useState([])
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')

  const submit = async () => {
    if (!replyText.trim() || busy) return
    setBusy(true)
    try {
      const r = await portal.post(`/api/notes/${note.id}/reply`, { content: replyText.trim(), images: replyImages })
      setReplyText(''); setReplyImages([])
      let m = ''
      if (r.notified?.length) m += `${r.notified.join(', ')}님에게 알림을 보냈습니다. `
      if (r.blocked?.length) m += `${r.blocked.join(', ')}님은 이 화면을 볼 수 없어 알림을 못 받습니다.`
      setMsg(m)
      onReply()
    } finally { setBusy(false) }
  }

  return (
    <div className="note-card">
      <div className="note-card-head">
        <b>{note.author_name}</b>
        <span className="muted">{fmtDateTime(note.created_at)}</span>
        {note.author_id === currentUserId && (
          <button type="button" className="note-delete" onClick={() => onDelete(note.id)}>삭제</button>
        )}
      </div>
      <div className="note-content">{note.content}</div>
      <ImageStrip images={note.images || []} onEnlarge={onEnlarge} />
      <div className="note-reply-box">
        <MentionTextarea value={replyText} onChange={setReplyText} mentionables={mentionables}
          placeholder="답글 (@이름으로 멘션)" onPasteImage={(d) => setReplyImages((p) => [...p, d])} />
        <ImageStrip images={replyImages} onRemove={(i) => setReplyImages((p) => p.filter((_, j) => j !== i))} onEnlarge={onEnlarge} />
        <button type="button" className="btn secondary" style={{ marginTop: 6 }} disabled={!replyText.trim() || busy} onClick={submit}>
          답글 등록
        </button>
        {msg && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{msg}</div>}
      </div>
    </div>
  )
}

export default function NoteDrawer({ target, screenKey, context, contextLabel, onNavigate,
  autoOpenNoteId, onAutoOpened, currentUserId }) {
  const [open, setOpen] = useState(false)
  const [notes, setNotes] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [mentionables, setMentionables] = useState([])
  const [composerText, setComposerText] = useState('')
  const [composerImages, setComposerImages] = useState([])
  const [busy, setBusy] = useState(false)
  const [saveMsg, setSaveMsg] = useState('')
  const [lightbox, setLightbox] = useState(null)
  const [err, setErr] = useState('')

  const qs = `target=${encodeURIComponent(target)}${screenKey ? `&screenKey=${encodeURIComponent(screenKey)}` : ''}`

  const loadList = () => {
    portal.get(`/api/notes/?${qs}`).then(setNotes).catch((e) => setErr(e.message))
  }
  const loadDetail = (id) => {
    portal.get(`/api/notes/${id}`).then((d) => setDetail(d)).catch((e) => setErr(e.message))
  }

  useEffect(() => {
    if (!open) return
    setErr('')
    loadList()
    portal.get(`/api/notes/mentionable${screenKey ? `?screenKey=${encodeURIComponent(screenKey)}` : ''}`)
      .then(setMentionables).catch(() => setMentionables([]))
  }, [open, target, screenKey])

  useEffect(() => {
    if (selectedId) loadDetail(selectedId)
    else setDetail(null)
  }, [selectedId])

  useEffect(() => {
    if (autoOpenNoteId) {
      setOpen(true)
      setSelectedId(autoOpenNoteId)
      onAutoOpened?.()
    }
  }, [autoOpenNoteId])

  const selectNote = (note) => {
    setSelectedId(note.id)
    onNavigate?.(note.context)
  }

  const submitNote = async () => {
    if (!composerText.trim() || busy) return
    setBusy(true); setSaveMsg('')
    try {
      const r = await portal.post('/api/notes/', {
        target, screenKey, content: composerText.trim(), images: composerImages,
        context, contextLabel,
      })
      setComposerText(''); setComposerImages([])
      let m = ''
      if (r.notified?.length) m += `${r.notified.join(', ')}님에게 알림을 보냈습니다. `
      if (r.blocked?.length) m += `${r.blocked.join(', ')}님은 이 화면을 볼 수 없어 알림을 못 받습니다.`
      setSaveMsg(m)
      loadList()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const deleteNote = async (id) => {
    try {
      await portal.del(`/api/notes/${id}`)
      if (selectedId === id) setSelectedId(null)
      loadList()
    } catch (e) { setErr(e.message) }
  }

  return (
    <>
      <button type="button" className="btn secondary note-drawer-btn" onClick={() => setOpen(true)}>
        🗒 서랍(메모) 열기{notes?.length ? ` (${notes.length})` : ''}
      </button>
      {open && (
        <div className="modal-backdrop" onMouseDown={() => setOpen(false)}>
          <div className="modal-panel note-drawer-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>서랍(메모)</h3>
              <button type="button" className="modal-close" onClick={() => setOpen(false)}>×</button>
            </div>
            {err && <div className="error-box" style={{ margin: '0 18px' }}>{err}</div>}
            <div className="note-drawer-body">
              <div className="note-list-pane">
                <div className="note-composer">
                  <MentionTextarea value={composerText} onChange={setComposerText} mentionables={mentionables}
                    placeholder="메모 작성 (@이름으로 멘션, 이미지는 Ctrl+V로 붙여넣기)"
                    onPasteImage={(d) => setComposerImages((p) => [...p, d])} />
                  <ImageStrip images={composerImages} onRemove={(i) => setComposerImages((p) => p.filter((_, j) => j !== i))} onEnlarge={setLightbox} />
                  <div className="row" style={{ marginTop: 6 }}>
                    {contextLabel && <span className="muted" style={{ fontSize: 12 }}>지금 보는 기간: {contextLabel}</span>}
                    <button type="button" className="btn" style={{ marginLeft: 'auto' }}
                      disabled={!composerText.trim() || busy} onClick={submitNote}>메모 남기기</button>
                  </div>
                  {saveMsg && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{saveMsg}</div>}
                </div>
                <div className="note-list">
                  {notes === null && <div className="muted">불러오는 중…</div>}
                  {notes?.length === 0 && <div className="muted">아직 메모가 없습니다</div>}
                  {notes?.map((n) => (
                    <div key={n.id} className={'note-list-item' + (n.id === selectedId ? ' sel' : '')}
                      onClick={() => selectNote(n)}>
                      <div className="note-list-item-head">
                        <b>{n.author_name}</b>
                        {n.context_label && <span className="note-period-badge">{n.context_label}</span>}
                      </div>
                      <div className="note-list-item-content">{n.content}</div>
                      <div className="muted" style={{ fontSize: 11 }}>
                        {fmtDateTime(n.created_at)}
                        {n.replyCount ? ` · 답글 ${n.replyCount}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="note-detail-pane">
                {!detail && <div className="muted">왼쪽에서 메모를 선택하세요</div>}
                {detail && (
                  <>
                    <NoteCard note={detail.note} mentionables={mentionables}
                      onReply={() => loadDetail(selectedId)} onDelete={deleteNote}
                      currentUserId={currentUserId} onEnlarge={setLightbox} />
                    <div className="note-replies">
                      {detail.replies.map((r) => (
                        <div key={r.id} className="note-reply-item">
                          <div className="note-card-head">
                            <b>{r.author_name}</b>
                            <span className="muted">{fmtDateTime(r.created_at)}</span>
                            {r.author_id === currentUserId && (
                              <button type="button" className="note-delete" onClick={() => deleteNote(r.id)}>삭제</button>
                            )}
                          </div>
                          <div className="note-content">{r.content}</div>
                          <ImageStrip images={r.images || []} onEnlarge={setLightbox} />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {lightbox && (
        <div className="modal-backdrop" style={{ zIndex: 300 }} onMouseDown={() => setLightbox(null)}>
          <img src={lightbox} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }} />
        </div>
      )}
    </>
  )
}
